import { Link } from 'react-router-dom';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

/* Podstawowe klocki interfejsu. Cel: czytelność na tablecie w magazynie —
   duże pola dotykowe, wyraźny fokus, żadnych ozdobników. */

type Wariant = 'glowny' | 'zwykly' | 'ostrzegawczy' | 'cichy';

const WARIANTY: Record<Wariant, string> = {
  glowny: 'bg-bordo-600 text-white hover:bg-bordo-700 active:bg-bordo-800 disabled:bg-kamien-300',
  zwykly: 'bg-white text-kamien-800 ring-1 ring-kamien-300 hover:bg-kamien-100 disabled:text-kamien-400',
  ostrzegawczy: 'bg-white text-status-brak ring-1 ring-status-brak/40 hover:bg-red-50',
  cichy: 'text-kamien-600 hover:text-bordo-600 hover:bg-bordo-50',
};

export function Przycisk({
  wariant = 'zwykly', children, className = '', ...reszta
}: ButtonHTMLAttributes<HTMLButtonElement> & { wariant?: Wariant }) {
  return (
    <button
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-sm
        font-medium transition-colors disabled:cursor-not-allowed ${WARIANTY[wariant]} ${className}`}
      {...reszta}
    >
      {children}
    </button>
  );
}

export function Pole({
  etykieta, blad, podpowiedz, id, className = '', ...reszta
}: InputHTMLAttributes<HTMLInputElement> & {
  etykieta: string; blad?: string; podpowiedz?: string;
}) {
  const idPola = id ?? `pole-${etykieta.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={idPola} className="text-sm font-medium text-kamien-700">{etykieta}</label>
      <input
        id={idPola}
        aria-invalid={blad ? true : undefined}
        aria-describedby={blad ? `${idPola}-blad` : podpowiedz ? `${idPola}-info` : undefined}
        className={`min-h-11 rounded-md border px-3 text-[15px] text-kamien-900 placeholder:text-kamien-400
          ${blad ? 'border-status-brak bg-red-50/40' : 'border-kamien-300 bg-white'} ${className}`}
        {...reszta}
      />
      {blad && <p id={`${idPola}-blad`} className="text-sm text-status-brak">{blad}</p>}
      {!blad && podpowiedz && (
        <p id={`${idPola}-info`} className="text-sm text-kamien-500">{podpowiedz}</p>
      )}
    </div>
  );
}

export function Wybor({
  etykieta, blad, id, children, ...reszta
}: SelectHTMLAttributes<HTMLSelectElement> & { etykieta: string; blad?: string }) {
  const idPola = id ?? `wybor-${etykieta.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={idPola} className="text-sm font-medium text-kamien-700">{etykieta}</label>
      <select
        id={idPola}
        aria-invalid={blad ? true : undefined}
        aria-describedby={blad ? `${idPola}-blad` : undefined}
        className={`min-h-11 rounded-md border bg-white px-3 text-[15px] text-kamien-900
          ${blad ? 'border-status-brak' : 'border-kamien-300'}`}
        {...reszta}
      >
        {children}
      </select>
      {blad && <p id={`${idPola}-blad`} className="text-sm text-status-brak">{blad}</p>}
    </div>
  );
}

export function Komunikat({
  rodzaj = 'info', tytul, children,
}: { rodzaj?: 'info' | 'blad' | 'sukces' | 'uwaga'; tytul?: string; children: ReactNode }) {
  const style = {
    info: 'bg-kamien-100 text-kamien-800 ring-kamien-300',
    blad: 'bg-red-50 text-status-brak ring-status-brak/30',
    sukces: 'bg-emerald-50 text-status-ok ring-status-ok/30',
    uwaga: 'bg-amber-50 text-status-uzupelnij ring-status-uzupelnij/30',
  }[rodzaj];
  return (
    <div role={rodzaj === 'blad' ? 'alert' : 'status'} className={`rounded-md px-4 py-3 text-sm ring-1 ${style}`}>
      {tytul && <p className="mb-1 font-semibold">{tytul}</p>}
      {children}
    </div>
  );
}

export function Plakietka({ children, ton = 'neutralny' }: {
  children: ReactNode; ton?: 'neutralny' | 'ok' | 'uwaga' | 'blad';
}) {
  const style = {
    neutralny: 'bg-kamien-100 text-kamien-700',
    ok: 'bg-emerald-50 text-status-ok',
    uwaga: 'bg-amber-50 text-status-uzupelnij',
    blad: 'bg-red-50 text-status-brak',
  }[ton];
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {children}
    </span>
  );
}

export function Karta({ tytul, opis, akcje, children }: {
  tytul?: string; opis?: string; akcje?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="rounded-lg bg-white ring-1 ring-kamien-200">
      {(tytul || akcje) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-kamien-200 px-5 py-4">
          <div>
            {tytul && <h2 className="text-base font-semibold text-kamien-900">{tytul}</h2>}
            {opis && <p className="mt-0.5 text-sm text-kamien-500">{opis}</p>}
          </div>
          {akcje}
        </header>
      )}
      {children}
    </section>
  );
}

export function Ladowanie({ tekst = 'Wczytywanie...' }: { tekst?: string }) {
  return <p className="px-5 py-8 text-center text-sm text-kamien-500">{tekst}</p>;
}

export function Zakladki<T extends string>({ wartosc, zmien, opcje }: {
  wartosc: T;
  zmien: (v: T) => void;
  opcje: { wartosc: T; etykieta: string; licznik?: number }[];
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-kamien-200">
      {opcje.map((o) => {
        const aktywna = o.wartosc === wartosc;
        return (
          <button
            key={o.wartosc}
            role="tab"
            aria-selected={aktywna}
            onClick={() => zmien(o.wartosc)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              aktywna
                ? 'border-bordo-600 text-bordo-700'
                : 'border-transparent text-kamien-600 hover:text-kamien-900'
            }`}
          >
            {o.etykieta}
            {o.licznik !== undefined && (
              <span className="ml-1.5 text-xs text-kamien-400">{o.licznik}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Pusto({ tytul, opis, akcja }: { tytul: string; opis?: string; akcja?: ReactNode }) {
  return (
    <div className="px-5 py-14 text-center">
      <p className="text-sm font-medium text-kamien-700">{tytul}</p>
      {opis && <p className="mx-auto mt-1 max-w-md text-sm text-kamien-500">{opis}</p>}
      {akcja && <div className="mt-4">{akcja}</div>}
    </div>
  );
}

export function PoleTekstowe({
  etykieta, blad, id, className = '', ...reszta
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { etykieta: string; blad?: string }) {
  const idPola = id ?? `ta-${etykieta.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={idPola} className="text-sm font-medium text-kamien-700">{etykieta}</label>
      <textarea
        id={idPola} rows={3}
        className={`rounded-md border px-3 py-2 text-[15px] text-kamien-900
          ${blad ? 'border-status-brak' : 'border-kamien-300'} bg-white ${className}`}
        {...reszta}
      />
      {blad && <p className="text-sm text-status-brak">{blad}</p>}
    </div>
  );
}

export function Przelacznik({ etykieta, opis, ...reszta }:
  InputHTMLAttributes<HTMLInputElement> & { etykieta: string; opis?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-1">
      <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-bordo-600" {...reszta} />
      <span>
        <span className="block text-sm font-medium text-kamien-800">{etykieta}</span>
        {opis && <span className="block text-xs text-kamien-500">{opis}</span>}
      </span>
    </label>
  );
}

/** Ostrzeżenie o atrapie leku — musi być widoczne wszędzie, gdzie pozycja się pojawia. */
export function AtrapaLeku() {
  return (
    <span
      className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-status-brak ring-1 ring-status-brak/30"
      title="Preparat symulacyjny — nie do użytku klinicznego"
    >
      nie do użytku klinicznego
    </span>
  );
}

/**
 * Lokalizacja na liście: pokazujemy samą nazwę miejsca, pełną ścieżkę dajemy
 * w podpowiedzi. Rozwinięta ścieżka („Budynek B — Collegium Medicum / 2 piętro /
 * Sala 214 — symulacja wysokiej wierności") zjada pół tabeli i nic nie wnosi,
 * bo i tak czyta się ostatni człon.
 */
/** Miejsce; gdy znamy jego identyfikator, nazwa prowadzi do pełnego widoku tej lokalizacji. */
export function Miejsce({ lokalizacja }: {
  lokalizacja: { id?: number; name: string; code: string; path?: string | null } | null;
}) {
  if (!lokalizacja) return <span className="text-kamien-400">—</span>;
  const nazwa = lokalizacja.id !== undefined ? (
    <Link
      to={`/lokalizacje/${lokalizacja.id}`}
      // Wiersze list otwierają formularz po kliknięciu — link ma tylko przejść do lokalizacji.
      onClick={(e) => e.stopPropagation()}
      className="text-kamien-600 hover:text-bordo-600 hover:underline"
    >
      {lokalizacja.name}
    </Link>
  ) : <span className="text-kamien-600">{lokalizacja.name}</span>;
  return (
    <span title={lokalizacja.path ?? lokalizacja.name} className="whitespace-nowrap">
      <span className="font-mono text-[11px] text-kamien-400">{lokalizacja.code}</span>{' '}
      {nazwa}
    </span>
  );
}
